<template>
    <div class="filter-card">
        <div class="filter-header">
            <div class="filter-title">{{ $t('common.filter') }}</div>
            <button-tooltip icon small :tooltip="$t('common.clear_filters')" @click="reset">
                <v-icon size="18" color="txtSecondary">mdi-filter-remove-outline</v-icon>
            </button-tooltip>
            <button-tooltip icon small :tooltip="$t('common.close')" @click="hide">
                <v-icon size="18" color="txtSecondary">$close</v-icon>
            </button-tooltip>
        </div>

        <div class="filter-body">
            <!-- SPORTS -->
            <div class="filter-group">
                <div class="filter-label">{{ $t('common.filter_sport') }}</div>
                <chip-selector :items="sports" :value-array="sportsSelected" @change="sportsSelected = $event" multiple
                    ref="sports" />
            </div>

            <!-- OBJECTIVES -->
            <div class="filter-group">
                <div class="filter-label">{{ $t('common.filter_objectives') }}</div>
                <chip-selector :items="objectives" :value-array="objectivesSelected"
                    @change="objectivesSelected = $event" multiple ref="objectives" />
            </div>

            <!-- COUNTRY -->
            <div class="filter-group">
                <div class="filter-label">{{ $t('common.filter_country') }}</div>
                <kimsa-combobox
                    :items="countryOptions"
                    item-text="label"
                    :placeholder="$t('common.search_place')"
                    @change="countrySelected = $event"
                    ref="places"
                />
            </div>

            <!-- RATING -->
            <div class="filter-group">
                <div class="filter-label">{{ $t('common.filter_rating') }}</div>
                <div class="rating-row">
                    <v-slider
                        v-model="minRating"
                        class="rating-slider"
                        min="0" max="5" step="1"
                        ticks="always" tick-size="3"
                        track-color="outline2" color="accent" thumb-color="accent"
                        hide-details
                    />
                    <rating-stars :value="minRating" size="15" />
                </div>
                <div class="rating-hint">{{ $t('common.filter_rating_hint') }}</div>
            </div>
        </div>

        <div class="filter-footer">
            <v-btn @click="filter" class="filter-cta" depressed block>
                {{ $t('common.filter') }}
            </v-btn>
        </div>
    </div>
</template>

<script>

import KimsaCombobox from '@/components/common/inputs/KimsaCombobox'
import ChipSelector from '@/components/common/inputs/ChipSelector'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import RatingStars from '@/components/common/RatingStars'

import { mapState, mapMutations, mapActions } from 'vuex'

export default {
    components: {
        KimsaCombobox,
        ChipSelector,
        ButtonTooltip,
        RatingStars,
    },
    data() {
        return {
            sportsSelected: [],
            objectivesSelected: [],
            countrySelected: '',
            minRating: 0,
        }
    },
    computed: {
        ...mapState(['config', 'countries']),
        objectives(vm = this) {
            if (!vm.config) return []
            return vm.config.objectives.map(objective => ({
                name: objective,
                value: objective,
                label: vm.objectiveLabel(objective)
            }))
        },
        sports(vm = this) {
            if (!vm.config) return []
            return vm.config.sports.map(sport => ({
                name: sport,
                value: sport,
                label: vm.sportLabel(sport)
            }))
        },
        countryOptions(vm = this) {
            return vm.countries.map(country => ({ ...country, label: vm.countryLabel(country.name) }))
        },
    },
    async mounted() {
        const vm = this
        if (!vm.config) await vm.bindConfig()
        if (vm.countries.length === 0) await vm.bindCountries()
    },
    methods: {
        ...mapMutations(['setFilterCoach']),
        ...mapActions(['bindConfig', 'bindCountries']),
        hide() {
            const vm = this
            vm.$emit('hide')
        },
        escape(value) {
            return String(value).replace(/'/g, "\\'")
        },
        reset() {
            const vm = this
            vm.$refs.sports.reset()
            vm.$refs.objectives.reset()
            vm.$refs.places.model = ''
            vm.sportsSelected = []
            vm.objectivesSelected = []
            vm.countrySelected = ''
            vm.minRating = 0
            vm.setFilterCoach()
        },
        filter() {
            const vm = this

            const sports = vm.sportsSelected.map(sport => vm.escape(sport))
            const objectives = vm.objectivesSelected.map(objective => vm.escape(objective))
            const country = vm.countrySelected

            let filter = '(accountType:coach AND readyToTrain=1) '
            if (sports.length > 0) filter += `AND (sport_areas:'${sports.join("' OR sport_areas:'")}') `
            if (objectives.length > 0) filter += `AND (objectives:'${objectives.join("' OR objectives:'")}') `
            if (country) filter += `AND (country.name:'${vm.escape(country.name)}') `
            if (vm.minRating > 0) filter += `AND ((rating.stars:${vm.minRating} TO 5)) `

            vm.setFilterCoach(filter)
            vm.$emit('hide')
        }
    },
}

</script>

<style scoped>

.filter-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--v-surface-base);
}

.filter-header {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    padding: 0 12px 0 18px;
    height: calc(56px + var(--safe-area-inset-top, 0px));
    padding-top: var(--safe-area-inset-top, 0px);
    border-bottom: 1px solid var(--v-outline-base);
}

.filter-title {
    flex: 1 1 auto;
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.filter-body {
    flex: 1 1 auto;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    min-height: 0;
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.filter-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--v-accent-base);
}

.theme--dark .filter-label {
    color: var(--v-primary-base);
}

.rating-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.rating-slider {
    flex: 1 1 auto;
}

.rating-hint {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.filter-footer {
    flex-shrink: 0;
    padding: 12px 18px;
    padding-bottom: calc(12px + var(--safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--v-outline-base);
}

.filter-cta {
    height: 44px !important;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
    color: #0C0E12 !important;
    background: var(--v-primary-base) !important;
}

</style>
